"use client";

import Link from "next/link";
import { MapPin, X } from "lucide-react";

type Agency = {
  id: number | string;
  nom: string;
  slug?: string;
  adresse?: string;
  telephone?: string;
};

export default function AgenciesPopup({
  isOpen,
  onClose,
  agences,
}: {
  isOpen: boolean;
  onClose: () => void;
  agences: Agency[];
}) {
  if (!isOpen) return null;

  const getAgencyNumber = (name: string) => {
    const match = name.match(/^\((\d+)\)/);
    return match ? Number(match[1]) : null;
  };

  const sortedAgences = [...agences].sort((a, b) => {
    const numA = getAgencyNumber(a.nom);
    const numB = getAgencyNumber(b.nom);

    // Les agences sans numéro vont en bas
    if (numA === null && numB === null) {
      return a.nom.localeCompare(b.nom, "fr");
    }
    if (numA === null) return 1;
    if (numB === null) return -1;

    // Tri croissant par numéro
    if (numA !== numB) {
      return numA - numB;
    }

    // Si même numéro, tri alphabétique
    return a.nom.localeCompare(b.nom, "fr");
  });

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm">
      <div className="relative w-full max-w-3xl rounded-[32px] bg-white p-6 shadow-[0_20px_50px_rgba(0,0,0,0.18)] md:p-8">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 text-black/60 transition hover:text-black"
        >
          <X className="h-6 w-6" />
        </button>

        <h2 className="pr-10 font-literata text-[28px] font-bold leading-[1.15] text-text-body md:text-[40px]">
          Nos agences
        </h2>

        <p className="mt-3 text-[16px] text-text-body/80 md:text-[18px]">
          Sélectionnez l’agence la plus proche de vous.
        </p>

        <div className="mt-8 grid max-h-[60vh] grid-cols-1 gap-4 overflow-y-auto pr-1 md:grid-cols-2">
          {sortedAgences.map((agency) => (
            <Link
              key={agency.id}
              href={agency.slug ? `/livraison-repas-entreprise/${agency.slug}` : "#"}
              className="group rounded-[24px] border border-black/5 bg-violet-light px-5 py-4 transition hover:-translate-y-1 hover:bg-pink hover:text-white hover:shadow-[0_12px_30px_rgba(221,34,96,0.18)]"
              onClick={onClose}
            >
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-pink transition group-hover:bg-white/15 group-hover:text-white">
                  <MapPin className="h-5 w-5" />
                </span>

                <div>
                  <p className="text-[20px] font-bold leading-[1.2]">
                    {agency.nom}
                  </p>

                  {agency.adresse && (
                    <p className="mt-1 text-[15px] leading-[1.35] text-text-body/75 transition group-hover:text-white/90">
                      {agency.adresse}
                    </p>
                  )}

                  {agency.telephone && (
                    <p className="mt-2 text-[15px] font-medium transition group-hover:text-white">
                      {agency.telephone}
                    </p>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}